import styled from '@emotion/styled'
import { zodResolver } from '@hookform/resolvers/zod'
import Link from 'next/link'
import React from 'react'
import { useForm } from 'react-hook-form'
import { z } from 'zod'

import { isEmpty } from '@livechat/data-utils'

import FeatureToggle from 'components/FeatureToggle'
import { FeatureExplanation, FeatureItem, FeaturesList } from 'components/FeaturesList'
import HoverableItem from 'components/HoverableItem'
import InfoBanner from 'components/InfoBanner'
import { Portal } from 'components/Portal'
import Form from 'components/fields/Form'
import Input from 'components/fields/Input'
import { ChatsIcon, InfoIcon, PhoneIcon } from 'components/icons'
import { useFeature } from 'hooks/useFeature'
import { useToast } from 'hooks/useToast'
import { HeadingControlPortalContext } from 'layouts/DefaultLayout'
import { updateFeature } from 'lib/api'
import { useAppStore } from 'lib/store'
import type { Feature } from 'types/feature'

const StyledLink = styled(Link)`
	color: ${({ theme }) => theme.colors.light.primaryTextColor};
`

const FeatureDescription = styled.div`
	display: flex;
	flex-direction: column;
	gap: ${({ theme }) => theme.spaces.space7};
	margin-bottom: 48px;
`

const FormSchema = z.object({
	pageName: z.string().nonempty('Page name is required').max(500, 'Cannot be longer than 500 characters'),
})

type FormValues = z.infer<typeof FormSchema>

function getFormValues(feature: Feature<'messenger'>): FormValues {
	return {
		pageName: feature.properties.pageName ?? '',
	}
}

function withDefaultValues(FormComponent: typeof MessengerForm) {
	const WithDefaultValues = React.forwardRef<HTMLFormElement, Props>((props, ref) => {
		const { feature: messengerFeature, isFeatureLoading: isMessengerFeatureLoading } = useFeature('messenger')

		if (!messengerFeature || isMessengerFeatureLoading) {
			return null
		}

		return (
			<FormComponent
				{...props}
				formRef={ref}
				firstSetup={isEmpty(messengerFeature.properties)}
				defaultValues={getFormValues(messengerFeature)}
			/>
		)
	})

	WithDefaultValues.displayName = `WithDefaultValues(${FormComponent.displayName})`

	return WithDefaultValues
}

function getPageNameFromInput(input: string): string {
	const pageName = input.includes('facebook.com/') ? input.split('facebook.com/')[1] : input
	return pageName.trim()
}

type Props = {
	id: string
	onSubmit?: VoidFunction
	onHasErrorsChanged?(hasErrors: boolean, id: string): void
	onIsDirtyChanged?(isDirty: boolean, id: string): void
	onIsSubmittingChanged?(isSubmitting: boolean, id: string): void
}

function MessengerForm({
	id,
	formRef,
	defaultValues,
	firstSetup,
	onHasErrorsChanged,
	onIsDirtyChanged,
	onIsSubmittingChanged,
	onSubmit,
}: Props & { defaultValues: FormValues; firstSetup: boolean; formRef: React.ForwardedRef<HTMLFormElement> }) {
	const showToast = useToast()
	const store = useAppStore()
	const { feature: messengerFeature, mutateFeature: mutateMessengerFeature } = useFeature('messenger')
	const { register, reset, watch, handleSubmit, formState } = useForm({
		defaultValues,
		mode: 'onTouched',
		resolver: zodResolver(FormSchema),
	})
	const { errors, isSubmitting, isDirty, isValid, dirtyFields } = formState
	const hasErrors = !isEmpty(errors)

	React.useEffect(() => {
		const handler = (values: Partial<FormValues>) => {
			store.dispatch({
				type: 'messenger/set',
				payload: {
					enabled: !!messengerFeature?.enabled,
					properties: {
						pageName: values.pageName,
					},
				},
			})
		}
		handler(defaultValues)
		const subscription = watch(handler)
		return () => subscription.unsubscribe()
	}, [store, watch, defaultValues, messengerFeature])

	React.useEffect(() => {
		onHasErrorsChanged?.(hasErrors, id)
	}, [id, onHasErrorsChanged, hasErrors])

	React.useEffect(() => {
		onIsSubmittingChanged?.(isSubmitting, id)
	}, [id, isSubmitting, onIsSubmittingChanged])

	React.useEffect(() => {
		onIsDirtyChanged?.(isDirty, id)
	}, [id, isDirty, onIsDirtyChanged])

	async function handleMessengerSwitchChange(isEnabled: boolean): Promise<{ isEnabled: boolean }> {
		try {
			const updatedFeature = await updateFeature('messenger', { enabled: isEnabled })
			mutateMessengerFeature(updatedFeature, { revalidate: false })
			showToast('success')
		} catch {
			showToast('error')
		}

		return { isEnabled }
	}

	async function _handleSubmit(data: FormValues) {
		if (!isValid || !isDirty) {
			return
		}

		try {
			const updatedFeature = await updateFeature('messenger', {
				...(firstSetup && { enabled: true }),
				...(!isEmpty(dirtyFields) && {
					properties: {
						...(dirtyFields.pageName && {
							pageName: getPageNameFromInput(data.pageName),
						}),
					},
				}),
			})

			mutateMessengerFeature(updatedFeature, { revalidate: false })
			reset(getFormValues(updatedFeature))
			onSubmit?.()
			showToast('success')
		} catch {
			showToast('error')
		}
	}

	const handleReset = (event: React.FormEvent) => {
		event.preventDefault()
		reset(defaultValues)
	}

	return (
		<>
			{!firstSetup && (
				<Portal context={HeadingControlPortalContext}>
					<FeatureToggle
						isEnabled={!!messengerFeature?.enabled}
						onChange={handleMessengerSwitchChange}
						ariaName="messenger"
						trackingName="Messenger"
					/>
				</Portal>
			)}
			<FeatureDescription>
				<FeatureExplanation>{`Handle customers' queries through the Meta Messenger application. Customers will be able to write to you using their desktop and mobile apps`}</FeatureExplanation>
				<FeaturesList>
					<FeatureItem>
						<ChatsIcon />
						Invites to chat when the widget is minimized
					</FeatureItem>
					<FeatureItem>
						<PhoneIcon />
						Opens up a Meta Messenger application
					</FeatureItem>
				</FeaturesList>
			</FeatureDescription>
			<Form id={id} ref={formRef} onSubmit={handleSubmit(_handleSubmit)} onReset={handleReset}>
				<Input
					required
					type="text"
					label="Page name"
					aria-label="profile"
					rightAdornment={
						<HoverableItem
							position={{ bottom: '45px', right: '25px' }}
							title="Paste your Facebook page name or the whole Facebook profile link"
						>
							<InfoIcon />
						</HoverableItem>
					}
					error={errors.pageName?.message}
					{...register('pageName')}
				/>
			</Form>
			<InfoBanner>
				{`When the widget is minimized, the customer will see a message with the same text and avatar as in the `}
				<StyledLink href="/configurator/widget-content/contact">Contact card →</StyledLink>
			</InfoBanner>
		</>
	)
}

MessengerForm.displayName = 'MessengerForm'

export default withDefaultValues(MessengerForm)
